<template>
    <div class="editor">
        <div class="d-flex justify-content-between justify-content-start">
            <editor-menu-bar :editor="editor" v-slot="{ commands, isActive, getMarkAttrs }">
                <div class="menubar">

                    <button
                        type="button"
                        class="btn btn-outline-dark btn-sm mb-2"
                        :class="{ 'btn-primary': isActive.bold() }"
                        @click="commands.bold"
                    >
                        <i class="fas fa-bold"></i>
                    </button>

                    <button
                        type="button"
                        class="btn btn-outline-dark btn-sm mb-2"
                        :class="{ 'btn-primary': isActive.italic() }"
                        @click="commands.italic"
                    >
                        <i class="fas fa-italic"></i>
                    </button>

                    <button
                        type="button"
                        class="btn btn-outline-dark btn-sm mb-2"
                        :class="{ 'btn-primary': isActive.strike() }"
                        @click="commands.strike"
                    >
                        <i class="fas fa-strikethrough"></i>
                    </button>

                    <button
                        type="button"
                        class="btn btn-outline-dark btn-sm mb-2"
                        :class="{ 'btn-primary': isActive.underline() }"
                        @click="commands.underline"
                    >
                        <i class="fas fa-underline"></i>
                    </button>

                    <button
                        type="button"
                        class="btn btn-outline-dark btn-sm mb-2"
                        :class="{ 'btn-primary': isActive.code_block() }"
                        @click="commands.code_block()"
                    >
                        <i class="fas fa-code"></i>
                    </button>

                    <button
                        type="button"
                        class="btn btn-outline-dark btn-sm mb-2"
                        :class="{ 'btn-primary': isActive.paragraph() }"
                        @click="commands.paragraph"
                    >
                        <i class="fas fa-paragraph"></i>
                    </button>

                    <button
                        type="button"
                        class="btn btn-outline-dark btn-sm mb-2"
                        :class="{ 'btn-primary': isActive.heading({ level: 1 }) }"
                        @click="commands.heading({ level: 1 })"
                    >
                        H1
                    </button>

                    <button
                        type="button"
                        class="btn btn-outline-dark btn-sm mb-2"
                        :class="{ 'btn-primary': isActive.heading({ level: 2 }) }"
                        @click="commands.heading({ level: 2 })"
                    >
                        H2
                    </button>

                    <button
                        type="button"
                        class="btn btn-outline-dark btn-sm mb-2"
                        :class="{ 'btn-primary': isActive.heading({ level: 3 }) }"
                        @click="commands.heading({ level: 3 })"
                    >
                        H3
                    </button>

                    <button
                        type="button"
                        class="btn btn-outline-dark btn-sm mb-2"
                        :class="{ 'btn-primary': isActive.bullet_list() }"
                        @click="commands.bullet_list"
                    >
                        <i class="fas fa-list"></i>
                    </button>

                    <button
                        type="button"
                        class="btn btn-outline-dark btn-sm mb-2"
                        :class="{ 'btn-primary': isActive.ordered_list() }"
                        @click="commands.ordered_list"
                    >
                        <i class="fas fa-list-ol"></i>
                    </button>

                    <button
                        type="button"
                        class="btn btn-outline-dark btn-sm mb-2"
                        :class="{ 'btn-primary': isActive.blockquote() }"
                        @click="commands.blockquote"
                    >
                        <i class="fas fa-quote-right"></i>
                    </button>

                    <button
                        type="button"
                        class="btn btn-outline-dark btn-sm mb-2"
                        @click="commands.undo"
                    >
                        <i class="fas fa-undo"></i>
                    </button>

                    <button
                        type="button"
                        class="btn btn-outline-dark btn-sm mb-2"
                        @click="commands.redo"
                    >
                        <i class="fas fa-redo"></i>
                    </button>

                    <button
                        type="button"
                        class="btn btn-outline-dark btn-sm mb-2"
                        @click="commands.createTable({rowsCount: 3, colsCount: 3, withHeaderRow: false })"
                    >
                        <i class="fas fa-table"></i>
                    </button>

                    <button
                        type="button"
                        class="btn btn-outline-dark btn-sm mb-2"
                        @click="showImagePrompt(commands.image)"
                    >
                        <i class="fas fa-image"></i>
                    </button>

                    <ul v-if="isActive.table()" class="list-inline font-12">
                        <li class="list-inline-item" @click="commands.addColumnBefore">Add Column Before</li>
                        <li class="list-inline-item" @click="commands.addColumnAfter">Add Column After</li>
                        <li class="list-inline-item" @click="commands.deleteColumn">Delete Column</li>
                        <li class="list-inline-item" @click="commands.addRowBefore">Add Row Before</li>
                        <li class="list-inline-item" @click="commands.addRowAfter">Add Row After</li>
                        <li class="list-inline-item" @click="commands.deleteRow">Delete Row</li>
                        <li class="list-inline-item" @click="commands.toggleCellMerge">Combine cells</li>
                    </ul>
                </div>
            </editor-menu-bar>
            <p>
                <a href="javascript:void(0)" @click="isShowHTML=false" :class="{'text-muted': isShowHTML}">Design</a> |
                <a href="javascript:void(0)" @click="isShowHTML=true" :class="{'text-muted': !isShowHTML}">HTML</a>
            </p>
        </div>
        <editor-content :editor="editor" class="editor-content" v-if="!isShowHTML"/>
        <div class="editor-content" v-else>
            {{ editor.getHTML() }}
        </div>
        <input type="hidden" :name="inputName" :value="editor.getHTML()">
    </div>
</template>

<script>
// Import the basic building blocks
import { Editor, EditorContent, EditorMenuBar } from 'tiptap'
import {
    Blockquote,
    CodeBlock,
    HardBreak,
    Heading,
    HorizontalRule,
    OrderedList,
    BulletList,
    ListItem,
    TodoItem,
    TodoList,
    Bold,
    Code,
    Italic,
    Link,
    Strike,
    Underline,
    History,
    Table,
    TableHeader,
    TableCell,
    TableRow,
    Image
} from 'tiptap-extensions'

export default {
    components: {
        EditorContent,
        EditorMenuBar
    },
    props: ['content', 'inputName'],
    data() {
        return {
            // Create an `Editor` instance with some default content. The editor is 
            // then passed to the `EditorContent` component as a `prop`
            editor: new Editor({
                content: this.content,
                extensions: [
                    new Blockquote(),
                    new BulletList(),
                    new CodeBlock(),
                    new HardBreak(),
                    new Heading({ levels: [1, 2, 3] }),
                    new HorizontalRule(),
                    new ListItem(),
                    new OrderedList(),
                    new TodoItem(),
                    new TodoList(),
                    new Link(),
                    new Bold(),
                    new Code(),
                    new Italic(),
                    new Strike(),
                    new Underline(),
                    new History(),
                    new Table({
                        resizable: true,
                    }),
                    new TableHeader(),
                    new TableCell(),
                    new TableRow(),
                    new Image({
                        resizable: true,
                    })
                ]
            }),
            linkUrl: null,
            linkMenuIsActive: false,
            isShowHTML: false,
        }
    },
    beforeDestroy() {
        // Always destroy your editor instance when it's no longer needed
        this.editor.destroy()
    },
    methods: {
        showImagePrompt(command) {
            const src = prompt('Enter the url of your image here')
            if (src !== null) {
                command({ src })
            }
        },
    }
}
</script>
<style type="text/css">
    .editor .editor-content {
        border: 1px solid #d3d3d3;
        min-height: 300px;
    }
    .editor .editor-content:focus,
    .editor .ProseMirror,
    .editor .ProseMirror:focus,
    .editor .ProseMirror-focused {
        border: none !important;
        outline: 0 !important;
    }
    .editor .btn {
        width: 31px;
        height: 31px;
    }
    .editor .btn-primary {
        color: #fff;
    }
    .editor li {
        cursor: pointer;
    }
    .editor li:after {
        content: '|';
        padding: 0 5px;
    }
    .editor .tableWrapper table td,
    .editor .tableWrapper table th {
        border: 2px solid #ddd;
        min-width: 1em;
    }
    .editor .editor-content img {
        max-width: 100%;
        height: auto;
    }
    

    .ProseMirror {
        min-height: 300px;
        padding: 0.532rem 1.25rem;
    }
</style>